import { HttpStatusCode } from "axios";

/**
 * Whether a collaboration load failed because the proposal is gone or is no longer this viewer's to
 * see, as opposed to failing transiently.
 *
 * The distinction decides whether a page may eject the viewer. Both proposal editors treated ANY load
 * error as "still resolving" and so kept waiting: a reviewer opening a deleted draft sat on a loading
 * indicator forever instead of being told and sent back. Redirecting on every error would be worse —
 * a blip would throw people out of a proposal they still have open — so only a settled, terminal
 * answer counts.
 *
 * This lives apart from `use-persisted-proposal-collaboration` deliberately. It is pure, and the
 * editors' specs mock that hook module wholesale; importing the predicate from there would make it
 * `undefined` under test and leave every page spec re-declaring a copy of this rule.
 */
export function isProposalNoLongerAvailableError(error: unknown): boolean {
  const status = proposalErrorStatus(error);
  // 404 gone, 410 deleted, 403 no longer permitted — each is a final answer about this proposal.
  return (
    status === HttpStatusCode.NotFound || status === HttpStatusCode.Gone || status === HttpStatusCode.Forbidden
  );
}

/**
 * Whether the server refused the read, as opposed to answering that there is nothing to read.
 *
 * The two are different answers and the pages must not tell the reader the first as if it were the
 * second. A vendor team member hired onto an unrestricted team is never a party to the negotiation,
 * so every proposal read they make answers 403 — and "Proposal not found" both misdescribes that and
 * hides the real reason behind obscurity the server did not ask for (#5665).
 */
export function isProposalAccessDeniedError(error: unknown): boolean {
  return proposalErrorStatus(error) === HttpStatusCode.Forbidden;
}

/**
 * What the pages say when the server refuses a proposal read. One text, because the three proposal
 * pages answer the same refusal and a reader who meets it on two of them must read the same thing.
 */
export const PROPOSAL_ACCESS_DENIED_MESSAGE = "You are not authorized to view this proposal";

function proposalErrorStatus(error: unknown): unknown {
  if (typeof error !== "object" || error === null) return undefined;
  return (error as { status?: unknown }).status;
}
